import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type DcdExplorerProduct } from '../lib/api';
import { fmt } from '../lib/format';

type Underlying = 'BTCUSDT' | 'ETHUSDT';
type TabFilter = 'all' | 'put' | 'call';

/** Sisa hari = selisih kalender. Jul 16 → Jul 17 = 1 day (sama kayak Binance) */
function daysUntilSettle(settleDate: string | undefined): number {
  if (!settleDate) return 0;
  const now = new Date();
  const settle = new Date(settleDate);
  if (!Number.isFinite(settle.getTime())) return 0;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(settle.getFullYear(), settle.getMonth(), settle.getDate());
  const diff = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  return diff > 0 ? diff : 0;
}

export function DcdExplorerPage() {
  const [underlying, setUnderlying] = useState<Underlying>('BTCUSDT');
  const [tabFilter, setTabFilter] = useState<TabFilter>('all');

  // Fetch DCD list
  const { data: dcdData, isLoading: dcdLoading } = useQuery({
    queryKey: ['dcd-list', underlying],
    queryFn: () => api.get<{ underlying: string; products: DcdExplorerProduct[]; count: number }>(
      `/api/binance/dcd/list?underlying=${underlying}`,
    ),
    refetchInterval: 60_000,
  });

  // Fetch spot price
  const { data: spotData } = useQuery({
    queryKey: ['price', underlying],
    queryFn: () => api.get<{ symbol: string; price: number }>(
      `/api/binance/price?symbol=${underlying}`,
    ),
    refetchInterval: 30_000,
  });

  // Fetch USDT/IDR rate
  const { data: usdtIdrData } = useQuery({
    queryKey: ['price', 'USDTIDR'],
    queryFn: () => api.get<{ symbol: string; price: number }>(
      '/api/binance/price?symbol=USDTIDR',
    ),
    refetchInterval: 60_000,
  });

  const spotPrice = spotData?.price ?? 0;
  const usdtIdrRate = usdtIdrData?.price ?? 0;
  const asset = underlying.replace('USDT', '');

  const products = dcdData?.products ?? [];

  // Filter ≤10 hari, sort: hari paling sedikit → strike paling rendah
  const putProducts = useMemo(
    () => products
      .filter((p) => p.optionType === 'PUT')
      .filter((p) => { const d = daysUntilSettle(p.settleDate || p.settlementDate); return d > 0 && d <= 10; })
      .sort((a, b) => {
        const da = daysUntilSettle(a.settleDate || a.settlementDate);
        const db = daysUntilSettle(b.settleDate || b.settlementDate);
        if (da !== db) return da - db;
        return Number(a.strikePrice) - Number(b.strikePrice);
      }),
    [products],
  );
  const callProducts = useMemo(
    () => products
      .filter((p) => p.optionType === 'CALL')
      .filter((p) => { const d = daysUntilSettle(p.settleDate || p.settlementDate); return d > 0 && d <= 10; })
      .sort((a, b) => {
        const da = daysUntilSettle(a.settleDate || a.settlementDate);
        const db = daysUntilSettle(b.settleDate || b.settlementDate);
        if (da !== db) return da - db;
        return Number(a.strikePrice) - Number(b.strikePrice);
      }),
    [products],
  );

  const showPut = tabFilter === 'all' || tabFilter === 'put';
  const showCall = tabFilter === 'all' || tabFilter === 'call';

  return (
    <div className="gl-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--gl-text)', letterSpacing: '-0.3px' }}>
            DCD Explorer
          </div>
          <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', marginTop: 4 }}>
            Browse semua produk DCD Binance — settlement ≤10 hari
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
          {spotPrice > 0 && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Spot {asset}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gl-text)', fontFamily: 'var(--font-mono)' }}>
                {fmt.usd(spotPrice, 2)}
              </div>
            </div>
          )}
          {usdtIdrRate > 0 && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>USDT/IDR</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gl-amber)', fontFamily: 'var(--font-mono)' }}>
                Rp {fmt.number(usdtIdrRate, 0)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls: underlying toggle + filter chips */}
      <div className="dcd-explorer__controls rvl">
        {/* Underlying toggle */}
        <div style={{ display: 'flex', gap: 8 }}>
          {(['BTCUSDT', 'ETHUSDT'] as Underlying[]).map((u) => (
            <button
              key={u}
              className={`dcd-explorer__toggle ${underlying === u ? 'dcd-explorer__toggle--active' : ''}`}
              onClick={() => setUnderlying(u)}
            >
              {u.replace('USDT', '')}
            </button>
          ))}
        </div>

        <div style={{ width: 1, height: 24, background: 'var(--gl-border)' }} />

        {/* Filter: All / Buy Low / Sell High */}
        <div style={{ display: 'flex', gap: 6 }}>
          {([
            { key: 'all' as TabFilter, label: 'Semua' },
            { key: 'put' as TabFilter, label: 'Buy Low' },
            { key: 'call' as TabFilter, label: 'Sell High' },
          ]).map((f) => (
            <button
              key={f.key}
              className={`dcd-explorer__filter ${tabFilter === f.key ? 'dcd-explorer__filter--active' : ''}`}
              onClick={() => setTabFilter(f.key)}
            >
              {f.key === 'put' && <span style={{ color: 'var(--gl-cyan)', marginRight: 4 }}>●</span>}
              {f.key === 'call' && <span style={{ color: 'var(--gl-amber)', marginRight: 4 }}>●</span>}
              {f.label}
            </button>
          ))}
        </div>

        {spotPrice > 0 && (
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gl-text-faint)' }}>
            1 {asset} = {fmt.usd(spotPrice, 2)}
          </div>
        )}
      </div>

      {/* Loading / Empty */}
      {dcdLoading ? (
        <div className="g-card rvl" style={{ marginTop: 16 }}>
          <div className="gl-empty">Memuat produk DCD...</div>
        </div>
      ) : products.length === 0 ? (
        <div className="g-card rvl" style={{ marginTop: 16 }}>
          <div className="gl-empty">Tidak ada produk DCD tersedia untuk {asset}</div>
        </div>
      ) : (
        <>
          {/* ==================== PUT (Buy Low) ==================== */}
          {showPut && (
            <div className="g-card rvl rvl-1" style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span className="gl-badge gl-badge--info">PUT</span>
                <span className="gl-label" style={{ marginBottom: 0 }}>
                  Buy Low — Beli {asset} di harga strike
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gl-text-faint)' }}>
                  {putProducts.length} produk · ≤10 hari · sort by hari → harga
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--gl-text-faint)', marginBottom: 16 }}>
                Invest <b style={{ color: 'var(--gl-text)' }}>USDT</b> = harga strike → jika {asset} ≤ strike saat settlement, kamu dapat {asset}
              </div>

              {putProducts.length === 0 ? (
                <div className="gl-empty">Tidak ada produk PUT</div>
              ) : (
                <table className="glt">
                  <thead>
                    <tr>
                      <th>Sisa Hari</th>
                      <th>Beli {asset} di</th>
                      <th>APR</th>
                      <th>Est. Profit (USDT)</th>
                      {usdtIdrRate > 0 && <th>Est. Profit (IDR)</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {putProducts.map((p) => {
                      const days = daysUntilSettle(p.settleDate || p.settlementDate);
                      const strike = Number(p.strikePrice) || 0;
                      const apr = Number(p.apr) || 0;
                      // Profit = strike × APR% × hari/365 (karena invest = strike)
                      const intUsdt = (strike > 0 && apr > 0 && days > 0)
                        ? strike * (apr / 100) * (days / 365)
                        : null;
                      const intIdr = intUsdt !== null && usdtIdrRate > 0 ? intUsdt * usdtIdrRate : null;
                      return (
                        <tr key={p.id}>
                          <td>
                            <span style={{ fontWeight: 600, color: days <= 2 ? 'var(--gl-rose)' : 'var(--gl-text)' }}>
                              {days > 0 ? days : '—'}
                            </span>
                            {days > 0 && <span style={{ fontSize: 11, color: 'var(--gl-text-faint)', marginLeft: 2 }}>hari</span>}
                          </td>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600 }}>
                            {fmt.usd(strike, 2)}
                          </td>
                          <td style={{ color: 'var(--gl-emerald)', fontWeight: 600 }}>
                            {fmt.percent(apr, 2)}
                          </td>
                          <td style={{ color: 'var(--gl-emerald)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
                            {intUsdt !== null ? fmt.usd(intUsdt, 2) : '—'}
                          </td>
                          {usdtIdrRate > 0 && (
                            <td style={{ color: 'var(--gl-amber)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                              {intIdr !== null ? `Rp ${fmt.number(intIdr, 0)}` : '—'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ==================== CALL (Sell High) ==================== */}
          {showCall && (
            <div className="g-card rvl rvl-2" style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span className="gl-badge gl-badge--warn">CALL</span>
                <span className="gl-label" style={{ marginBottom: 0 }}>
                  Sell High — Jual {asset} di harga strike
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gl-text-faint)' }}>
                  {callProducts.length} produk · ≤10 hari · sort by hari → harga
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--gl-text-faint)', marginBottom: 16 }}>
                Invest <b style={{ color: 'var(--gl-text)' }}>1 {asset}</b> → jika {asset} ≥ strike saat settlement, {asset} kamu dijual di harga strike
              </div>

              {callProducts.length === 0 ? (
                <div className="gl-empty">Tidak ada produk CALL</div>
              ) : (
                <table className="glt">
                  <thead>
                    <tr>
                      <th>Sisa Hari</th>
                      <th>Jual {asset} di</th>
                      <th>APR</th>
                      <th>Est. Profit (USDT)</th>
                      {usdtIdrRate > 0 && <th>Est. Profit (IDR)</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {callProducts.map((p) => {
                      const days = daysUntilSettle(p.settleDate || p.settlementDate);
                      const strike = Number(p.strikePrice) || 0;
                      const apr = Number(p.apr) || 0;
                      // Profit = 1 BTC × spot × APR% × hari/365
                      const intUsdt = (spotPrice > 0 && apr > 0 && days > 0)
                        ? spotPrice * (apr / 100) * (days / 365)
                        : null;
                      const intIdr = intUsdt !== null && usdtIdrRate > 0 ? intUsdt * usdtIdrRate : null;
                      return (
                        <tr key={p.id}>
                          <td>
                            <span style={{ fontWeight: 600, color: days <= 2 ? 'var(--gl-rose)' : 'var(--gl-text)' }}>
                              {days > 0 ? days : '—'}
                            </span>
                            {days > 0 && <span style={{ fontSize: 11, color: 'var(--gl-text-faint)', marginLeft: 2 }}>hari</span>}
                          </td>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600 }}>
                            {fmt.usd(strike, 2)}
                          </td>
                          <td style={{ color: 'var(--gl-emerald)', fontWeight: 600 }}>
                            {fmt.percent(apr, 2)}
                          </td>
                          <td style={{ color: 'var(--gl-emerald)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
                            {intUsdt !== null ? fmt.usd(intUsdt, 2) : '—'}
                          </td>
                          {usdtIdrRate > 0 && (
                            <td style={{ color: 'var(--gl-amber)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                              {intIdr !== null ? `Rp ${fmt.number(intIdr, 0)}` : '—'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {/* Legend */}
      <div className="dcd-explorer__legend rvl rvl-3">
        <span style={{ color: 'var(--gl-text-faint)', fontSize: 11 }}>
          <b style={{ color: 'var(--gl-cyan)' }}>PUT (Buy Low):</b> Invest USDT = strike → profit = strike × APR × hari / 365 &nbsp;|&nbsp;
          <b style={{ color: 'var(--gl-amber)' }}>CALL (Sell High):</b> Invest 1 {asset} → profit = spot × APR × hari / 365 &nbsp;|&nbsp;
          Hari ≤ 2 ditandai merah (settlement sangat dekat)
        </span>
      </div>
    </div>
  );
}
